import { useEffect, useMemo, useState } from 'react'

import cn from 'classnames'

import { ChainType } from '@lifi/types'
import { Account } from '@lifi/wallet-management'

import { ChainId } from '@src/configs/chainInfo'
import { useWalletConnections } from '@src/hooks/useWalletConnections'
import { useAuthContext } from '@src/providers/AuthProvider'
import { useApplicationStore } from '@src/zustand_stores/application.store'
import { truncateAddress } from '@src/utils/truncateAddress'
import { analyticsService } from '@src/services/analytics'

import { isOriginEVM, isOriginSolana, isOriginTron } from '@src/utils/chainUtils'
import { getConnectorIcon } from '@lifi/wallet-management'

/**
 * Props for the ConnectButton component
 * @property {ChainId} requiredChainType - The specific chain type required for the current context
 * @property {boolean} enforceEVM - Whether to enforce EVM wallet connection regardless of context
 */
interface ConnectButtonProps
    extends React.DetailedHTMLProps<React.ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement> {
    requiredChainType?: ChainId
    enforceEVM?: boolean
}

const ConnectButton = ({ className, requiredChainType, enforceEVM = false, ...props }: ConnectButtonProps) => {
    // ======================================================================
    // STATE AND HOOKS INITIALIZATION
    // ======================================================================
    const { account, accounts, connectHandler, isEVMConnected, isSolanaConnected } = useAuthContext()
    const { walletButtonDisplayChainId } = useApplicationStore()
    const { isTronConnected, tronWalletAddress } = useWalletConnections()
    const openWalletDrawer = useApplicationStore(state => state.openWalletDrawer)
    
    // Custom connect handler that tracks the wallet_main_attempt_connect event
    const handleConnect = () => {
        // Determine wallet type based on context
        let walletType = 'EVM/Solana' // Default
        
        if (requiredChainType === ChainId.Solana) {
            walletType = 'Solana'
        } else if (requiredChainType === ChainId.Tron) {
            walletType = 'Tron'
        } else if (enforceEVM) {
            walletType = 'EVM'
        }
        
        // Track the main wallet connection attempt
        analyticsService.trackEvent('wallet_main_attempt_connect', {
            wallet_type: walletType,
            button_location: 'header_nav', // This is the button in the top navigation
            timestamp: new Date().toISOString()
        })
        
        // Call the original connect handler
        connectHandler()
    }

    // UI state
    const [networkImage, setNetworkImage] = useState(`assets/network/ethereum.png`)
    const [truncatedAddress, setTruncatedAddress] = useState('')
    const [connectedWallets, setConnectedWallets] = useState<{ image: string; address: string }[]>([])
    const [urlHasSwapParams, setUrlHasSwapParams] = useState(false)

    // Chain-specific flags
    const isTronChain = isOriginTron(walletButtonDisplayChainId)

    // ======================================================================
    // URL MONITORING FOR SWAP PARAMETERS
    // ======================================================================
    useEffect(() => {
        // Initial check for swap parameters
        const checkForSwapParams = () => {
            const queryString = window.location.search.substring(1)
            const hasParams = queryString.includes('sellChain=') || queryString.includes('buyChain=')
            setUrlHasSwapParams(hasParams)
        }

        // Check immediately
        checkForSwapParams()

        // Add popstate event listener for browser navigation
        window.addEventListener('popstate', checkForSwapParams)

        // Set up an interval to check for URL changes
        // This is necessary since history-throttled changes don't trigger popstate
        const intervalId = setInterval(checkForSwapParams, 250)

        return () => {
            window.removeEventListener('popstate', checkForSwapParams)
            clearInterval(intervalId)
        }
    }, [])

    // ======================================================================
    // WALLET REQUIREMENT DETERMINATION LOGIC
    // ======================================================================
    // Determine what wallet type is needed based on requiredChainType
    const needsSolanaWallet = useMemo(() => {
        if (enforceEVM) return false // Never need Solana wallet if enforceEVM is true
        if (requiredChainType) return isOriginSolana(requiredChainType)
        return false
    }, [requiredChainType, enforceEVM])

    const needsEVMWallet = useMemo(() => {
        if (enforceEVM) return true // Always need EVM wallet if enforceEVM is true
        if (requiredChainType) return isOriginEVM(requiredChainType)
        return false
    }, [requiredChainType, enforceEVM])

    const needsTronWallet = useMemo(() => {
        if (enforceEVM) return false
        if (requiredChainType) return isOriginTron(requiredChainType)
        return false
    }, [requiredChainType, enforceEVM])

    // Check if the required wallet type is connected
    const isRequiredWalletConnected = useMemo(() => {
        if (enforceEVM || needsEVMWallet) {
            return isEVMConnected
        }
        if (needsSolanaWallet) {
            return isSolanaConnected
        }
        if (needsTronWallet) {
            return isTronConnected
        }
        return isEVMConnected || isSolanaConnected || isTronConnected
    }, [needsSolanaWallet, needsEVMWallet, isEVMConnected, isSolanaConnected, isTronConnected, enforceEVM])

    // ======================================================================
    // BUTTON LABEL DETERMINATION
    // ======================================================================
    // Determine appropriate button label based on required wallet type
    const buttonLabel = useMemo(() => {
        // If no swap parameters, always return generic 'Connect Wallet'
        if (!urlHasSwapParams) return 'Connect Wallet'

        if (enforceEVM) return 'Connect EVM Wallet'
        if (needsSolanaWallet && !isSolanaConnected) return 'Connect Solana Wallet'
        if (needsEVMWallet && !isEVMConnected) return 'Connect EVM Wallet'
        if (needsTronWallet && !isTronConnected) return 'Connect Tron Wallet'
        return 'Connect Wallet'
    }, [
        needsSolanaWallet,
        needsEVMWallet,
        isSolanaConnected,
        isEVMConnected,
        enforceEVM,
        urlHasSwapParams,
        isTronConnected,
        needsTronWallet,
    ])

    // ======================================================================
    // EVENT HANDLERS
    // ======================================================================
    const handleConnectedClick = () => {
        openWalletDrawer(true)
    }

    // ======================================================================
    // CONNECTED WALLETS STATE MANAGEMENT
    // ======================================================================
    useEffect(() => {
        const wallets: { image: string; address: string }[] = []

        // First check for EVM wallets
        const evmAccounts = accounts?.filter(acc => acc.chainType === ChainType.EVM) || []
        evmAccounts.forEach(evmAcc => {
            if (evmAcc.address) {
                wallets.push({
                    image: evmAcc.connector
                        ? getConnectorIcon(evmAcc.connector) || `assets/network/ethereum.png`
                        : `assets/network/ethereum.png`,
                    address: evmAcc.address,
                })
            }
        })

        // Then check for Solana wallets
        const solanaAccounts = accounts?.filter(acc => acc.chainType === ChainType.SVM) || []
        solanaAccounts.forEach(solAcc => {
            if (solAcc.address) {
                wallets.push({
                    image: solAcc.connector
                        ? getConnectorIcon(solAcc.connector) || `assets/network/solana.png`
                        : `assets/network/solana.png`,
                    address: solAcc.address,
                })
            }
        })

        // Finally check for Tron wallet
        if (isTronConnected && tronWalletAddress) {
            wallets.push({
                image: `assets/network/tron.png`,
                address: tronWalletAddress,
            })
        }

        setConnectedWallets(wallets)

        // Set the primary wallet for display in the button text (prioritize current account)
        if (account?.address) {
            const connectorIcon = getConnectorIcon(account.connector)

            if (account.chainType === ChainType.EVM) {
                setNetworkImage(connectorIcon || `assets/network/ethereum.png`)
                setTruncatedAddress(truncateAddress(account.address))
            } else if (account.chainType === ChainType.SVM) {
                setNetworkImage(connectorIcon || `assets/network/solana.png`)
                setTruncatedAddress(truncateAddress(account.address))
            }
        } else if (accounts?.length > 0) {
            // If current account is not available, check all accounts
            const evmAccount = accounts.find(acc => acc.chainType === ChainType.EVM)
            const solanaAccount = accounts.find(acc => acc.chainType === ChainType.SVM)

            if (evmAccount?.address) {
                setNetworkImage(
                    evmAccount.connector
                        ? getConnectorIcon(evmAccount.connector) || `assets/network/ethereum.png`
                        : `assets/network/ethereum.png`
                )
                setTruncatedAddress(truncateAddress(evmAccount.address))
            } else if (solanaAccount?.address) {
                setNetworkImage(
                    solanaAccount.connector
                        ? getConnectorIcon(solanaAccount.connector) || `assets/network/solana.png`
                        : `assets/network/solana.png`
                )
                setTruncatedAddress(truncateAddress(solanaAccount.address))
            }
        }

        // Tron handling for primary display
        if (isTronConnected && tronWalletAddress && !accounts?.length) {
            setNetworkImage(`assets/network/tron.png`)
            setTruncatedAddress(truncateAddress(tronWalletAddress))
        }
    }, [account, accounts, isTronConnected, tronWalletAddress, walletButtonDisplayChainId])

    // ======================================================================
    // UI RENDERING FUNCTIONS
    // ======================================================================
    // Render connected wallet icons in overlapping pattern
    const renderWalletIcons = () => {
        if (connectedWallets.length === 0) return null

        return (
            <div className='flex items-center'>
                {connectedWallets.map((wallet, index) => (
                    <div
                        key={`${wallet.address}-${index}`}
                        className='rounded-full border border-gray-200 bg-white'
                        style={{ marginLeft: index > 0 ? '-0.75rem' : '0', zIndex: connectedWallets.length - index }}
                    >
                        <img
                            src={wallet.image}
                            alt={`wallet-${index}`}
                            className='h-6 w-6'
                            onError={e => {
                                e.currentTarget.src = '/assets/token/unknown.png'
                            }}
                        />
                    </div>
                ))}
            </div>
        )
    }

    // Determine what to display based on connected accounts
    const renderButton = () => {
        // Case 2: Enforcing EVM wallet with no EVM connection
        if (enforceEVM && !isEVMConnected) {
            return (
                <button className={cn('custom-wallet-button', className)} onClick={handleConnect} {...props}>
                    <img src={'assets/network/ethereum.png'} alt='evm' className='h-6 w-6' />
                    Connect EVM Wallet
                </button>
            )
        }

        // Case 3: Required wallet not connected
        if (!isRequiredWalletConnected) {
            return (
                <button className={cn('custom-wallet-button', className)} onClick={handleConnect} {...props}>
                    {buttonLabel.includes('EVM') && (
                        <img src={'assets/network/ethereum.png'} alt='evm' className='h-6 w-6' />
                    )}
                    {buttonLabel.includes('Solana') && (
                        <img src={'assets/network/solana.png'} alt='solana' className='h-6 w-6' />
                    )}
                    {buttonLabel.includes('Tron') && (
                        <img src={'assets/network/tron.png'} alt='tron' className='h-6 w-6' />
                    )}
                    {buttonLabel}
                </button>
            )
        }

        // Case 4: Fallback check for EVM enforcement
        if (enforceEVM && !isEVMConnected) {
            return (
                <button className={cn('custom-wallet-button', className)} onClick={handleConnect} {...props}>
                    <img src={'assets/network/ethereum.png'} alt='evm' className='h-6 w-6' />
                    Connect EVM Wallet
                </button>
            )
        }

        // Case 5: Default case - wallet connected
        return (
            <button className={cn('custom-wallet-button flex items-center', className)} onClick={handleConnectedClick}>
                {renderWalletIcons()}
                <span>{truncatedAddress}</span>
            </button>
        )
    }

    // ======================================================================
    // COMPONENT RENDER
    // ======================================================================
    return <div className='relative'>{renderButton()}</div>
}

export default ConnectButton
